/**
 * @summary Функция, принимающая любой параметр и проверяющая его на undefined.
 *
 * @param x Любой элемент, который нужно проверить на undefined.
 */

type Diff<T, U> = T extends U ? never : T;

export const notUndefined = <T>(x: T): Diff<T, undefined> => {
  if (x === undefined) {
    throw new Error('Assertion failed: parameter is undefined.');
  }
  return x as Diff<T, undefined>;
};

/**
 * @summary Функция, принимающая любой параметр и проверяющая его на null.
 *
 * @param x Любой элемент, который нужно проверить на null.
 */
export const notNull = <T>(x: T): Diff<T, null> => {
  if (x === null) {
    throw new Error(`Assertion failed: parameter is null.`);
  }
  return x as Diff<T, null>;
};

/**
 * @summary Функция сравнения(преимущественно, для таблицы). Достаточно универсальная.
 *
 * @param prev - Прошлый элемент для сравнения.
 * @param next - Следующий элемент для сравнения.
 * @param columnName - Название столбца.
 */
export const compare = <K extends string>(
  prev: Record<K, any>,
  next: Record<K, any>,
  columnName: K
): number => {
  const prevValue = prev[columnName];
  const nextValue = next[columnName];

  if (prevValue === nextValue) {
    return 0;
  }
  if (prevValue === null) {
    return -1;
  }
  if (nextValue === null) {
    return 1;
  }

  const prevType = typeof prevValue;
  const nextType = typeof nextValue;

  if (prevType !== nextType) {
    throw new Error(`Different types can not be compared (${prevType} vs ${nextType}).`);
  }

  switch (prevType) {
    case 'string':
      return prevValue.localeCompare(nextValue);
    case 'boolean':
    case 'number':
      return prevValue - nextValue;
    default:
      throw new Error(`Unknown type ${prevType} while comparing.`);
  }
};
